import React from "react";
import { ResponsiveLine } from "@nivo/line";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

const SERIES = [
  { id: "Current Demand",    color: "#2E93fA", gradId: "gradCurrent"    },
  { id: "Integrated Demand", color: "#A53838", gradId: "gradIntegrated" },
  { id: "Target Demand",     color: "#26BF00", gradId: "gradTarget"     },
  { id: "Forecast",          color: "#DC6CFE", gradId: "gradForecast"   },
];

const GEYSER = { id: "Geyser Demand", color: "#FDB940", gradId: "gradGeyser" };

const BG      = "#0a0e1a";
const PANEL   = "#0f1420";
const GRID    = "#151c2e";
const LABEL   = "#8892a4";

const nivoTheme = {
  background: PANEL,
  textColor: LABEL,
  fontSize: 11,
  axis: {
    domain: { line: { stroke: "#1e2a40", strokeWidth: 1 } },
    legend: { text: { fill: LABEL, fontSize: 11 } },
    ticks: {
      line: { stroke: "#1e2a40", strokeWidth: 1 },
      text: { fill: LABEL, fontSize: 10 },
    },
  },
  grid: { line: { stroke: GRID, strokeWidth: 1 } },
  crosshair: { line: { stroke: "#ffffff22", strokeWidth: 1, strokeDasharray: "4 4" } },
  tooltip: { container: { background: "transparent", padding: 0, border: "none", boxShadow: "none" } },
};

function makeDefs(series) {
  return series.map((s) => ({
    id: s.gradId,
    type: "linearGradient",
    colors: [
      { offset: 0,   color: s.color, opacity: 0.5 },
      { offset: 50,  color: s.color, opacity: 0.15 },
      { offset: 100, color: s.color, opacity: 0 },
    ],
  }));
}

function makeFills(series) {
  return series.map((s) => ({ match: { id: s.id }, id: s.gradId }));
}

function CustomPoint({ x, y, color }) {
  return (
    <g>
      <circle cx={x} cy={y} r={6} fill={BG} stroke={color} strokeWidth={2} strokeOpacity={0.8} />
      <circle cx={x} cy={y} r={3} fill={color} fillOpacity={0.9} />
    </g>
  );
}

function SliceTooltip({ slice }) {
  return (
    <Box
      sx={{
        bgcolor: "#0d1526ee",
        border: "1px solid #1e3050",
        borderRadius: 2,
        p: 1.5,
        minWidth: 190,
        backdropFilter: "blur(8px)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.7)",
      }}
    >
      <Typography variant="caption" sx={{ color: "#4a6fa5", display: "block", mb: 0.8, fontWeight: 700, letterSpacing: 0.5 }}>
        {slice.points[0]?.data.xFormatted}
      </Typography>
      {slice.points.map((p) => (
        <Box key={p.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 0.4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.color, boxShadow: `0 0 6px ${p.color}` }} />
            <Typography variant="caption" sx={{ color: "#8892a4" }}>{p.serieId}</Typography>
          </Box>
          <Typography variant="caption" sx={{ color: "#e0e8f8", fontWeight: 700 }}>
            {Number(p.data.y).toFixed(3)} MVA
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function NivoChart({ series, defs, fills, yLabel, sparse = false }) {
  const hasData = series.some((s) => s.data.length > 0);
  if (!hasData) {
    return (
      <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ color: "#2a3550" }}>No data available</Typography>
      </Box>
    );
  }

  return (
    <ResponsiveLine
      data={series}
      theme={nivoTheme}
      colors={(s) => s.color}
      defs={defs}
      fill={fills}
      margin={{ top: 24, right: 32, bottom: 60, left: 64 }}
      xScale={{ type: "time", format: "native", precision: "minute" }}
      xFormat="time:%d %b %H:%M"
      yScale={{ type: "linear", stacked: false, nice: true }}
      axisLeft={{
        legend: yLabel,
        legendOffset: -52,
        legendPosition: "middle",
        format: (v) => v.toFixed(2),
        tickSize: 0,
        tickPadding: 8,
      }}
      axisBottom={{
        format: "%H:%M",
        tickValues: "every 2 hours",
        tickSize: 0,
        tickPadding: 8,
      }}
      curve="monotoneX"
      lineWidth={1.5}
      enableArea={true}
      areaOpacity={1}
      enableGridX={false}
      enableGridY={true}
      useMesh={true}
      enableSlices="x"
      motionConfig="gentle"
      pointSymbol={({ datum, color }) =>
        datum.y !== null ? <CustomPoint x={0} y={0} color={color} /> : null
      }
      pointSize={sparse ? 8 : 0}
      enableCrosshair={true}
      crosshairType="x"
      sliceTooltip={SliceTooltip}
      legends={[
        {
          anchor: "bottom",
          direction: "row",
          justify: false,
          translateY: 56,
          itemsSpacing: 20,
          itemWidth: 130,
          itemHeight: 12,
          itemTextColor: LABEL,
          symbolSize: 8,
          symbolShape: "circle",
        },
      ]}
    />
  );
}

function ChartWrapper({ title, children }) {
  return (
    <Box sx={{ mb: 5 }}>
      <Typography
        variant="overline"
        sx={{ color: "#4a6fa5", mb: 1.5, display: "block", fontWeight: 700, letterSpacing: 2 }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          bgcolor: PANEL,
          borderRadius: 3,
          p: 2,
          height: 400,
          border: "1px solid #1a2540",
          boxShadow: "0 4px 40px rgba(0,0,0,0.5)",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export default function NivoLineTemplate({ microServiceGraphData, targetDataNow, microserviceForcastState }) {
  const demandSeries = [
    {
      id: "Current Demand",
      color: "#2E93fA",
      data: microServiceGraphData
        .map((i) => ({ x: new Date(i.interval), y: i.current_demand ? +(i.current_demand / 1000000).toFixed(4) : null }))
        .filter((d) => d.y !== null),
    },
    {
      id: "Integrated Demand",
      color: "#A53838",
      data: microServiceGraphData
        .map((i) => ({ x: new Date(i.interval), y: i.latest_integrated_demand ? +(i.latest_integrated_demand / 1000000).toFixed(4) : null }))
        .filter((d) => d.y !== null),
    },
    {
      id: "Target Demand",
      color: "#26BF00",
      data: microServiceGraphData
        .map((i) => ({ x: new Date(i.interval), y: targetDataNow?.target ? +(targetDataNow.target / 1000000).toFixed(4) : null }))
        .filter((d) => d.y !== null),
    },
    {
      id: "Forecast",
      color: "#DC6CFE",
      data: microServiceGraphData
        .map((i) => ({ x: new Date(i.interval), y: microserviceForcastState?.[0]?.forecast ? +(microserviceForcastState[0].forecast / 1000000).toFixed(4) : null }))
        .filter((d) => d.y !== null),
    },
  ];

  const geyserSeries = [
    {
      id: "Geyser Demand",
      color: "#FDB940",
      data: microServiceGraphData
        .map((i) => ({ x: new Date(i.interval), y: i.geyser_demand ? +(i.geyser_demand / 1000000).toFixed(4) : null }))
        .filter((d) => d.y !== null),
    },
  ];

  return (
    <Box sx={{ bgcolor: BG, borderRadius: 3, p: 3, border: "1px solid #111827" }}>
      <ChartWrapper title="Demand Overview — MVA">
        <NivoChart
          series={demandSeries}
          defs={makeDefs(SERIES)}
          fills={makeFills(SERIES)}
          yLabel="MVA"
        />
      </ChartWrapper>
      <ChartWrapper title="Geyser (Tjommie) Demand — MVA">
        <NivoChart
          series={geyserSeries}
          defs={makeDefs([GEYSER])}
          fills={makeFills([GEYSER])}
          yLabel="MVA"
          sparse
        />
      </ChartWrapper>
    </Box>
  );
}
